// FrontBanner - MODULE
// ***********************************************************************************************************************
import { Box, Checkbox, FormControlLabel, Grid, MenuItem, Select } from "@mui/material";
import * as React from "react";
import { DashboardCardProgress } from "source/components/framework/DashboardCard/DashboardCardProgress";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { Base } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/framework/base";
import { AppUtils } from "../../../framework/appUtils";
import * as store from "../../../framework/customStore";
import { SaveDataObj } from "../../../framework/saveData";
import { IFrontPageBanner } from "../../../models/front/frontPageBanner";
import { IFrontPageBannerImage } from "../../../models/front/frontPageBannerImage";
import * as frontService from "../../../models/services/frontService";
import { FilterButtons } from "../../framework/Filters/FilterButtons";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../framework/Grid/GridRow";
import { PageDrawer } from "../../framework/Page/PageDrawer";
import { AppSettingsIcon } from "source/components/framework/Icon/AppSettingsIcon";
import { frontSetBannerImageChangeInterval } from "source/models/store/front";

//FrontBannerPropertiesDialog
export interface IFrontBannerPropertiesDialogProp {
    classes?: string;
    panelVisible: boolean;
    imageChangeInterval: number;
    onSavePanelSize: (panelVisible: boolean) => void;
    onClose: () => void;
}

export interface IFrontBannerPropertiesDialogState {
    panelVisible: boolean;
    imageChangeInterval: string;
}

export class FrontBannerPropertiesDialog extends React.Component<IFrontBannerPropertiesDialogProp, IFrontBannerPropertiesDialogState> {
    private orgStateHash: string = "";

    constructor(props: IFrontBannerPropertiesDialogProp) {
        super(props);
        this.state = {
            panelVisible: props.panelVisible,
            imageChangeInterval: props.imageChangeInterval.toString(10),
        };
        const saveData = this.getSaveDataFromState(props, this.state);
        this.orgStateHash = saveData.hash;
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "imageChangeInterval") {
            this.setState({ imageChangeInterval: value });
        }
    };

    changePanelVisibility = (panelVisible: boolean) => {
        this.setState({ panelVisible });
    };

    getSaveDataFromState = (props: IFrontBannerPropertiesDialogProp, state: IFrontBannerPropertiesDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("imageChangeInterval", state.imageChangeInterval.toInteger());
        //Only for change detection
        result.append("panelVisible", state.panelVisible);
        return result;
    };

    saveToDb = async () => {
        const obj = this;
        const props = this.props;
        const state = this.state;
        const saveData = obj.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(() => frontService.saveBanner(saveData.obj), false);
        if (!result) return;
        store.customStore.dispatch(frontSetBannerImageChangeInterval(state.imageChangeInterval.toInteger()));
        //Close dialog
        props.onClose();
        //Save panel size
        props.onSavePanelSize(state.panelVisible);
    };

    handleSave = () => {
        this.saveToDb();
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.saveToDb();
        } else {
            props.onClose();
        }
    };

    render() {
        const state = this.state;
        return (
            <PageDrawer
                open
                icon={<AppSettingsIcon />}
                title={Translations.Banner}
                parentTitle={Translations.Panels}
                onClose={this.handleCancel}
            >
                <Box display="flex" flexDirection="column" flexGrow={2} margin="24px">
                    <Box display="flex" flexDirection="column" flexGrow={2}>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormControlLabel
                                    label={Translations.PanelVisible}
                                    control={
                                        <Checkbox
                                            checked={state.panelVisible}
                                            onChange={(_e, checked) => this.changePanelVisibility(checked)}
                                        />
                                    }
                                />
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.ImageChangeInterval}>
                                    <Select name="imageChangeInterval" title={Translations.ImageChangeInterval} value={state.imageChangeInterval} onChange={this.handleChange}>
                                        <MenuItem key={0} value="0">0 s</MenuItem>
                                        <MenuItem key={10} value="10">10 s</MenuItem>
                                        <MenuItem key={30} value="30">30 s</MenuItem>
                                        <MenuItem key={60} value="60">1 min</MenuItem>
                                        <MenuItem key={300} value="300">5 min</MenuItem>
                                        <MenuItem key={600} value="600">10 min</MenuItem>
                                        <MenuItem key={1800} value="1800">30 min</MenuItem>
                                        <MenuItem key={3600} value="3600">1 h</MenuItem>
                                    </Select>
                                </FormField>
                            </Grid>
                        </GridRow>
                    </Box>
                    <FilterButtons
                        chartPanelPropertiesMode
                        onCancel={this.handleCancel}
                        onSave={this.handleSave}
                    />
                </Box>
            </PageDrawer>
        );
    }
}

// FrontBanner
export interface IFrontBannerProp {
    banner: IFrontPageBanner;
}

interface IFrontBannerState {
    images: IFrontPageBannerImage[];
    imageChangeInterval: number;
    imageIndex: number;
}

export class FrontBanner extends React.Component<IFrontBannerProp, IFrontBannerState> {
    private refreshTimer = 0;

    clearRefreshTimer = () => {
        if (this.refreshTimer !== 0) {
            window.clearTimeout(this.refreshTimer);
            this.refreshTimer = 0;
        }
    };

    setRefreshTimer = (imageChangeInterval: number) => {
        const obj = this;
        this.clearRefreshTimer();
        if (!imageChangeInterval) return;
        this.refreshTimer = window.setTimeout(() => {
            const state = this.state;
            if (!this.props.banner.images || this.props.banner.images?.length < 1) {
                obj.clearRefreshTimer();
                return;
            }
            obj.setState({
                imageIndex: state.images.length > 0 ? ((state.imageIndex + 1) % state.images.length) : -1
            });
            obj.setRefreshTimer(imageChangeInterval);
        }, this.state.imageChangeInterval * 1000);
    };

    constructor(props: IFrontBannerProp) {
        super(props);
        const images = props.banner.images && props.banner.images ? props.banner.images.slice(0) : [];
        this.state = {
            images,
            imageChangeInterval: props.banner.images && props.banner.imageChangeInterval ? props.banner.imageChangeInterval : 0,
            imageIndex: images.length > 0 ? 0 : -1,
        };
    }

    componentDidUpdate(prevProps: IFrontBannerProp, prevState: IFrontBannerState) {
        const props = this.props;
        if (!props.banner.images) return;
        const prevHash = Base.joinSuffix(prevProps.banner.images && prevProps.banner.images ? prevProps.banner.images.map(i => i.id) : [], "#") + (prevProps.banner.images && prevProps.banner.imageChangeInterval ? prevProps.banner.imageChangeInterval : 0).toString(10);
        const curHash = Base.joinSuffix(props.banner.images && props.banner.images ? props.banner.images.map(i => i.id) : [], "#") + (props.banner.images && props.banner.imageChangeInterval ? props.banner.imageChangeInterval : 0).toString(10);
        if (prevHash === curHash) return;
        const imageChangeInterval = props.banner.images && props.banner.imageChangeInterval ? props.banner.imageChangeInterval : 0;
        this.setState({
            imageIndex: props.banner.images.length > 0 ? 0 : -1,
            images: props.banner.images.slice(0),
            imageChangeInterval
        });
        this.setRefreshTimer(imageChangeInterval);
    }

    componentDidMount(): void {
        this.setRefreshTimer(this.state.imageChangeInterval);
    }

    componentWillUnmount(): void {
        this.clearRefreshTimer();
    }

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <Box sx={{
                position: "relative",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 1rem",
            }}>
                {state.images.map((images, i) =>
                    <Box
                        component="img"
                        key={images.id}
                        src={images.image}
                        sx={{
                            position: "absolute",
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            opacity: (state.imageIndex === i) ? 1 : 0,
                            transition: "opacity 1s ease-in-out",
                        }}
                    />
                )}
                <DashboardCardProgress isLoading={state.imageIndex < 0 && !props.banner.images} />
            </Box>
        );
    }
}